/**
 * adaptiveApplication.ts — MER Algo Footprints v8.5.1
 *
 * Implements the bounded active adaptation layer between advisory learning
 * (v8.5.0) and governance controls (v8.5.2).
 *
 * Architecture position:
 *   Outcome/Attribution → Advisory Learning → [THIS MODULE] → Governance → UI
 *
 * Bounded Active Adaptation Application Layer.
 *
 * ── Responsibility ────────────────────────────────────────────────────────────
 * This module applies learned modifiers to BASE outputs AFTER all engines run.
 * It NEVER mutates original engine outputs — it returns new adjusted values.
 *
 * ── Safety rails (all mandatory) ─────────────────────────────────────────────
 * 1. ENABLE_ACTIVE_ADAPTATION must be true in config (default: false)
 * 2. sampleSize >= MIN_SAMPLE_SIZE required per modifier
 * 3. learnedModifier must be within [MIN_MODIFIER, MAX_MODIFIER]
 * 4. Every applied modifier produces a full AdaptiveAuditEntry
 * 5. When flag is false → passthrough, identical to advisory mode output
 *
 * ── Replay safety ────────────────────────────────────────────────────────────
 * With ENABLE_ACTIVE_ADAPTATION = false:
 *   - Outputs are IDENTICAL to v8.5.0 advisory mode
 *   - No modifier is applied
 *   - Audit trail is empty
 *
 * With ENABLE_ACTIVE_ADAPTATION = true:
 *   - Outputs are deterministic from (baseValue, learnedModifier)
 *   - Full audit trail is produced
 *   - Same inputs always produce same outputs
 */

import {
  AdaptiveWeightProfile,
  SignalCalibrationProfile,
  AdaptiveAuditEntry,
  RegimeKey,
  ADAPTIVE_LEARNING_THRESHOLDS as T,
} from './adaptiveLearning';

// ── Explicit adaptation target allowlist ─────────────────────────────────────
//
// Centralized declaration of what MAY and MAY NOT be adapted.
// This is the authoritative contract — governance enforces it, not individual callers.

export const ADAPTIVE_TARGETS = {
  // ✓ ALLOWED — post-engine interpretation scalars
  CONFIDENCE:        true,   // thesis/signal confidence scaling
  ATTRIBUTION:       true,   // attribution layer weight adjustment
  RANKING:           true,   // signal ranking / prioritization scores
  WARNING_SEVERITY:  true,   // warning emphasis weighting

  // ✗ FORBIDDEN — core engine math and structural calculations
  CORE_ENGINE_MATH:          false,   // gamma formulas, liquidity scoring
  STRUCTURE_CALCULATION:     false,   // wall/flip-zone/level math
  TRAP_CLASSIFICATION:       false,   // trap rule definitions
  VOLATILITY_REGIME_MATH:    false,   // vol regime thresholds
  REPLAY_EVENT_LOGIC:        false,   // replay intelligence event extraction
  MARKET_DATA_NORMALIZATION: false,   // raw data normalization
} as const;

export type AdaptationTarget = 'confidence' | 'ranking' | 'attribution' | 'warning_severity';

/** Named applied-modifier state — this is what governance manages */
export interface AppliedModifier {
  signalKey:       string;
  target:          AdaptationTarget;
  baseValue:       number;
  learnedModifier: number;
  effectiveValue:  number;
  reason:          string;
  sampleSize:      number;
  regimeContext:   RegimeKey | null;
  appliedRule:     string;
}

/**
 * Explainability record produced for every active modifier application.
 * Alias of AdaptiveAuditEntry — kept here for clear v8.5.1 attribution;
 * governance (v8.5.2) consumes AdaptiveAuditEntry from adaptiveLearning.ts directly.
 */
export interface AdaptiveApplicationAudit {
  signalKey:      string;
  target:         AdaptationTarget;
  baseValue:      number;
  modifier:       number;
  adjustedValue:  number;
  reason:         string;
  sampleSize:     number;
  regimeContext:  RegimeKey | null;
  appliedRule:    string;
}

// ── Feature flag ──────────────────────────────────────────────────────────────
//
// Controls whether active modifiers are applied at all.
// Defaults to false — system behaves identically to v8.5.0 advisory mode.
//
// Set REACT_APP_ENABLE_ACTIVE_ADAPTATION=true in .env to enable.
// Switching OFF requires no rebuild — re-reads config on each call.

export function isActiveAdaptationEnabled(): boolean {
  return process.env.REACT_APP_ENABLE_ACTIVE_ADAPTATION === 'true';
}

// ── Adaptive modifier application ─────────────────────────────────────────────

export interface AdaptiveApplicationResult {
  /** Adjusted value (= baseValue when flag is off or sample insufficient) */
  adjustedValue: number;
  /** Whether a modifier was actually applied */
  modifierApplied: boolean;
  /** Full audit entry — null when no modifier applied */
  auditEntry: AdaptiveAuditEntry | null;
}

/**
 * Apply a learned confidence modifier to a base confidence value.
 *
 * SAFE BY DEFAULT: returns baseValue unchanged when:
 *   - ENABLE_ACTIVE_ADAPTATION is false
 *   - profile sample size < MIN_SAMPLE_SIZE
 *   - modifier is neutral (= 1.0)
 *   - modifier outside [MIN_MODIFIER, MAX_MODIFIER] (safety clamp, shouldn't occur)
 *
 * @param baseConfidence  — original confidence value (0–100)
 * @param calibProfile    — calibration profile from buildAdaptiveLearningProfile()
 * @param regimeContext   — structured regime context at application time
 */
export function applyConfidenceModifier(
  baseConfidence: number,
  calibProfile:   SignalCalibrationProfile,
  regimeContext:  RegimeKey,
): AdaptiveApplicationResult {
  const noChange = { adjustedValue: baseConfidence, modifierApplied: false, auditEntry: null };

  if (!isActiveAdaptationEnabled())            return noChange;
  if (calibProfile.sampleSize < T.MIN_SAMPLE_SIZE) return noChange;
  if (calibProfile.suggestedAdjustment === null)    return noChange;

  const modifier  = Math.max(T.MIN_MODIFIER, Math.min(T.MAX_MODIFIER, calibProfile.suggestedAdjustment));
  if (Math.abs(modifier - T.NEUTRAL_MODIFIER) < 0.001) return noChange;

  const adjusted = Math.round(Math.min(100, Math.max(0, baseConfidence * modifier)) * 10) / 10;

  const auditEntry: AdaptiveAuditEntry = {
    signalKey:     calibProfile.signalKey,
    baseValue:     baseConfidence,
    modifier,
    adjustedValue: adjusted,
    reason:        calibProfile.explanation,
    regimeContext,
    sampleSize:    calibProfile.sampleSize,
    appliedRule:   `APPLY_CONFIDENCE_${calibProfile.confidenceBias.toUpperCase()}`,
  };

  return { adjustedValue: adjusted, modifierApplied: true, auditEntry };
}

/**
 * Apply a learned attribution weight modifier to a base weight.
 *
 * @param baseWeight      — original attribution weight (0–1)
 * @param weightProfile   — weight profile from buildAdaptiveLearningProfile()
 * @param regimeContext   — structured regime context
 */
export function applyWeightModifier(
  baseWeight:    number,
  weightProfile: AdaptiveWeightProfile,
  regimeContext: RegimeKey,
): AdaptiveApplicationResult {
  const noChange = { adjustedValue: baseWeight, modifierApplied: false, auditEntry: null };

  if (!isActiveAdaptationEnabled())                return noChange;
  if (weightProfile.sampleSize < T.MIN_SAMPLE_SIZE) return noChange;
  if (Math.abs(weightProfile.learnedModifier - T.NEUTRAL_MODIFIER) < 0.001) return noChange;

  const modifier  = Math.max(T.MIN_MODIFIER, Math.min(T.MAX_MODIFIER, weightProfile.learnedModifier));
  const adjusted  = Math.round(Math.max(0, Math.min(1, baseWeight * modifier)) * 1000) / 1000;

  const auditEntry: AdaptiveAuditEntry = {
    signalKey:     weightProfile.layer,
    baseValue:     baseWeight,
    modifier,
    adjustedValue: adjusted,
    reason:        weightProfile.reason,
    regimeContext,
    sampleSize:    weightProfile.sampleSize,
    appliedRule:   weightProfile.appliedRule,
  };

  return { adjustedValue: adjusted, modifierApplied: true, auditEntry };
}

/**
 * Apply all available modifiers from a learning profile to a named output map.
 *
 * Returns the adjusted values and a complete audit trail for every modifier applied.
 * When ENABLE_ACTIVE_ADAPTATION is false, returns all base values unchanged.
 *
 * @param baseValues     — map of signalKey → base value (0–100 for confidence, 0–1 for weights)
 * @param weightProfiles — from AdaptiveLearningProfile.weightProfiles
 * @param calibProfiles  — from AdaptiveLearningProfile.calibrationProfiles
 * @param regimeContext  — structured regime context at application time
 */
export function applyAdaptiveModifiers(
  baseValues:     Record<string, number>,
  weightProfiles: AdaptiveWeightProfile[],
  calibProfiles:  SignalCalibrationProfile[],
  regimeContext:  RegimeKey,
): { adjustedValues: Record<string, number>; auditTrail: AdaptiveAuditEntry[] } {
  const adjustedValues: Record<string, number> = { ...baseValues };
  const auditTrail: AdaptiveAuditEntry[] = [];

  if (!isActiveAdaptationEnabled()) {
    return { adjustedValues, auditTrail };
  }

  // Apply weight modifiers
  for (const wp of weightProfiles) {
    if (!(wp.layer in baseValues)) continue;
    const result = applyWeightModifier(baseValues[wp.layer], wp, regimeContext);
    if (result.modifierApplied) {
      adjustedValues[wp.layer] = result.adjustedValue;
      auditTrail.push(result.auditEntry!);
    }
  }

  // Apply calibration modifiers (global profile → 'confidence' key)
  const globalCalib = calibProfiles.find(c => c.signalKey === 'global');
  if (globalCalib && 'confidence' in baseValues) {
    const result = applyConfidenceModifier(baseValues['confidence'], globalCalib, regimeContext);
    if (result.modifierApplied) {
      adjustedValues['confidence'] = result.adjustedValue;
      auditTrail.push(result.auditEntry!);
    }
  }

  // Sort audit trail by signalKey for deterministic ordering
  auditTrail.sort((a, b) => a.signalKey.localeCompare(b.signalKey));

  return { adjustedValues, auditTrail };
}

/**
 * Build the current applied-modifier state as a structured list.
 * This is the shape that v8.5.2 governance manages (proposals, approvals, rollbacks).
 *
 * Returns the full list of AppliedModifier entries for every non-neutral modifier
 * that WOULD be applied given the current profiles.
 * Does NOT require the feature flag to be on — governance needs to see proposals
 * in advisory mode too.
 *
 * PURE FUNCTION — deterministic from inputs.
 */
export function buildAppliedModifierState(
  weightProfiles: AdaptiveWeightProfile[],
  calibProfiles:  SignalCalibrationProfile[],
  regimeContext:  RegimeKey,
): AppliedModifier[] {
  const result: AppliedModifier[] = [];

  for (const wp of weightProfiles) {
    if (wp.sampleSize < T.MIN_SAMPLE_SIZE) continue;
    if (Math.abs(wp.learnedModifier - T.NEUTRAL_MODIFIER) < 0.001) continue;
    const modifier = Math.max(T.MIN_MODIFIER, Math.min(T.MAX_MODIFIER, wp.learnedModifier));
    result.push({
      signalKey:       wp.layer,
      target:          'attribution',
      baseValue:       wp.baseWeight,
      learnedModifier: modifier,
      effectiveValue:  Math.round(wp.baseWeight * modifier * 1000) / 1000,
      reason:          wp.reason,
      sampleSize:      wp.sampleSize,
      regimeContext,
      appliedRule:     wp.appliedRule,
    });
  }

  for (const cp of calibProfiles) {
    if (cp.sampleSize < T.MIN_SAMPLE_SIZE) continue;
    if (cp.suggestedAdjustment === null) continue;
    if (Math.abs(cp.suggestedAdjustment - T.NEUTRAL_MODIFIER) < 0.001) continue;
    const modifier = Math.max(T.MIN_MODIFIER, Math.min(T.MAX_MODIFIER, cp.suggestedAdjustment));
    result.push({
      signalKey:       `calib:${cp.signalKey}`,
      target:          'confidence',
      baseValue:       1.0,
      learnedModifier: modifier,
      effectiveValue:  modifier,
      reason:          cp.explanation,
      sampleSize:      cp.sampleSize,
      regimeContext,
      appliedRule:     `CALIB_${cp.confidenceBias.toUpperCase()}`,
    });
  }

  // Deterministic sort: target then signalKey
  return result.sort((a, b) => a.target.localeCompare(b.target) || a.signalKey.localeCompare(b.signalKey));
}
